/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { ISalesOrder } from './components/get-sales-order';
import { IHeader, IList, IOrderHistoryViewProps } from './order-history-search';
import { Form, FormGroup, Button, Input } from 'reactstrap';

export const OrderHistoryOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    salesId,
    receiptId,
    channelName,
    createdDate,
    count,
    amount
    // channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {salesId}
        {receiptId}
        {createdDate}
        {count}
        {amount}
        {/* {channelReferenceId} */}
    </Node>
);

export const OrderHistoryGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo
}) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {processing}
        {address}
        {trackingInfo}
    </Node>
);

export const OrderHistoryGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderHistoryGroupDelivery {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map((salesLine: ISalesLine) => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

export const OrderHistoryGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderHistoryGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderHistoryHeader: React.FC<IHeader> = ({ headerProps, heading, orderCountLabel, extraActions }) => (
    <Node {...headerProps}>
        {heading}
        {orderCountLabel}
        {extraActions}
    </Node>
);

export const OrderHistorySalesOder: React.FC<ISalesOrder> = ({
    salesOrderProps,
    orderInfomation,
    groups,
    orderDetailsLink,
    expandProductsButton
}) => {
    return (
        <Node {...salesOrderProps}>
            {orderInfomation && <OrderHistoryOrderInfomation {...orderInfomation} />}
            {groups && <OrderHistoryGroups {...groups} />}
            {expandProductsButton}
            {orderDetailsLink}
        </Node>
    );
};

export const OrderHistorySearch: React.FC<IOrderHistoryViewProps> = ({ searchPlaceholderText }) => (
    <div>
        <form>
            <input type='text' placeholder={searchPlaceholderText?.toString()}></input>
            <input type='submit' value='Submit'></input>
        </form>
    </div>
);

export const OrderHistoryList: React.FC<IList> = ({ listProps, salesOrders }) => (
    <Node {...listProps}>
        {salesOrders && salesOrders.map((salesOrder: ISalesOrder, index: number) => <OrderHistorySalesOder key={index} {...salesOrder} />)}
    </Node>
);

const _renderOrderSearchInput = (
    getHistory: (searchFilter: string) => void,
    searchPlaceholderText: string | undefined,
    searchFilter: string,
    setSearchFilter: (value: string) => void
): React.ReactNode => {
    const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchFilter(e.target.value);
    };
    return (
        <div className='ms-order-history__search'>
            <FormGroup>
                <Form
                    // onSubmit={event => handleSearchSubmit(getHistory, event, setSearchFilter, searchFilter)}
                    class='ms-order-history__form'
                    id='search-order-history-form'
                >
                    <Input
                        name='search-value'
                        placeholder={searchPlaceholderText}
                        value={searchFilter}
                        onChange={handleValueChange}
                        className='ms-search__form-control'
                    />
                </Form>
            </FormGroup>
        </div>
    );
};

const handleSearchSubmit = (
    getHistory: (searchFilter: string) => void,
    e: any,
    setSearchFilter: (value: string) => void,
    searchFilter: string
) => {
    e.preventDefault();
    getHistory(searchFilter);
};

const _renderSearchSubmitButton = (
    getHistory: (searchFilter: string) => void,
    searchPlaceholderText: string | undefined,
    searchFilter: string,
    setSearchFilter: (value: string) => void
): React.ReactNode => {
    return (
        <Button
            className='eve-order-history-search-container-bottom__button'
            form='search-order-history-form'
            type='submit'
            onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
                handleSearchSubmit(getHistory, event, setSearchFilter, searchFilter)
            }
        >
            Search
        </Button>
    );
};

const OrderHistoryView: React.FC<IOrderHistoryViewProps> = ({
    orderHistoryProps,
    header,
    alert,
    loading,
    emptyMessage,
    backToShoppingLink,
    list,
    table,
    moreButton,
    getHistory,
    viewState: { isLoading },
    backOrderOnlyCheckbox,
    backOrderOnlyToggle,
    datePickers,
    showDatePickers,
    searchByPoCheckbox,
    searchByPoToggle,
    searchPlaceholderText
}) => {
    const [searchFilter, setSearchFilter] = React.useState<string>('');

    return (
        <Module {...orderHistoryProps}>
            <Node className='eve-order-history-search-container'>
                <div className='eve-order-history-search-container-top'>
                    {_renderOrderSearchInput(getHistory, searchPlaceholderText, searchFilter, setSearchFilter)}
                </div>
                <div className='eve-order-history-search-container-middle'>
                    {searchByPoCheckbox && searchByPoToggle}
                    {backOrderOnlyCheckbox && backOrderOnlyToggle}
                    {showDatePickers && datePickers}
                </div>
                <div className='eve-order-history-search-container-bottom'>
                    {_renderSearchSubmitButton(getHistory, searchPlaceholderText, searchFilter, setSearchFilter)}
                </div>
            </Node>
            {header && <OrderHistoryHeader {...header} />}
            {isLoading && loading}
            {alert && (
                <>
                    {alert}
                    {backToShoppingLink}
                </>
            )}
            {!isLoading && emptyMessage && (
                <>
                    {emptyMessage}
                    {backToShoppingLink}
                </>
            )}
            {!isLoading && list && <OrderHistoryList {...list} />}
            {table}
            {moreButton && moreButton}
        </Module>
    );
};

export default OrderHistoryView;
